import React from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Trash, Star } from "@phosphor-icons/react";

const TAX_SUGGESTIONS = ["Simples Nacional (DAS)", "ISS", "IRPJ / CSLL", "PIS / COFINS", "INSS pró-labore"];
const FEE_SUGGESTIONS = ["Pix / Dinheiro", "Débito", "Crédito à vista", "Crédito parcelado (até 6x)", "Crédito parcelado (até 12x)", "Antecipação de recebíveis", "Link de pagamento"];

export function ItemListEditor({ title, hint, items, onChange, nameLabel, suggestions, withDefault, testId }) {
  const update = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const remove = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    if (withDefault && next.length && !next.some((x) => x.is_default)) next[0].is_default = true;
    onChange(next);
  };
  const add = (name = "") => onChange([...items, { name, percentage: 0, ...(withDefault ? { is_default: items.length === 0 } : {}) }]);
  const setDefault = (i) => onChange(items.map((it, idx) => ({ ...it, is_default: idx === i })));
  const total = items.reduce((s, it) => s + (parseFloat(it.percentage) || 0), 0);
  const used = new Set(items.map((i) => i.name));

  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-heading font-semibold text-lg">{title}</h3>
        {!withDefault && <span className="text-sm text-slate-600">Total: <strong className="font-mono-nums" data-testid={`${testId}-total`}>{total.toFixed(2)}%</strong></span>}
      </div>
      <p className="text-xs text-slate-500 mb-3">{hint}</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${testId}-row-${i}`}>
            {withDefault && (
              <button type="button" onClick={() => setDefault(i)} title="Usar como padrão na tabela de preços" className={`shrink-0 ${it.is_default ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}`} data-testid={`${testId}-default-${i}`}>
                <Star size={20} weight={it.is_default ? "fill" : "regular"} />
              </button>
            )}
            <Input value={it.name} onChange={(e) => update(i, "name", e.target.value)} placeholder={nameLabel} className="flex-1" data-testid={`${testId}-name-${i}`} />
            <div className="relative w-28">
              <Input type="number" step="0.01" min="0" value={it.percentage} onChange={(e) => update(i, "percentage", e.target.value)} className="pr-7 font-mono-nums" data-testid={`${testId}-pct-${i}`} />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-500">%</span>
            </div>
            <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`${testId}-remove-${i}`}><Trash size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button type="button" variant="outline" size="sm" onClick={() => add()} data-testid={`${testId}-add`}><Plus size={14} className="mr-1" /> Adicionar</Button>
        {suggestions.filter((s) => !used.has(s)).map((s) => (
          <button key={s} type="button" onClick={() => add(s)} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700" data-testid={`${testId}-suggest`}>+ {s}</button>
        ))}
      </div>
      {withDefault && <p className="text-xs text-slate-500 mt-2"><Star size={12} weight="fill" className="inline text-amber-500" /> O meio marcado como padrão define a tabela de preços. Os demais aparecem como "preço por forma de pagamento" em cada procedimento.</p>}
    </div>
  );
}

export function TaxesEditor({ items, onChange }) {
  return <ItemListEditor title="Impostos" hint="Liste cada tributo que incide sobre o faturamento. No Simples Nacional, o ISS já está dentro do DAS — não some os dois." items={items} onChange={onChange} nameLabel="Nome do imposto" suggestions={TAX_SUGGESTIONS} testId="taxes" />;
}

export function PaymentFeesEditor({ items, onChange }) {
  return <ItemListEditor title="Meios de pagamento e taxas" hint="Cadastre cada operadora/forma de recebimento com sua taxa efetiva (inclua antecipação quando usar)." items={items} onChange={onChange} nameLabel="Ex.: Crédito à vista - Stone" suggestions={FEE_SUGGESTIONS} withDefault testId="fees" />;
}

export function LossField({ value, onChange }) {
  return (
    <div>
      <Label>Reserva para inadimplência (%)</Label>
      <Input type="number" step="0.1" min="0" value={value} onChange={onChange} className="mt-1 max-w-[160px]" data-testid="settings-loss" />
      <p className="text-xs text-slate-500 mt-1">Percentual do faturamento que você perde com calotes/cancelamentos. Use 0 se recebe tudo no cartão (o risco fica com a operadora).</p>
    </div>
  );
}
